import { createFileRoute, Link as RouterLink } from "@tanstack/react-router";
import { Contact as ContactIcon, Plus } from "lucide-react";
import {
  Banner,
  Button,
  EmptyState,
  ListSkeleton,
  PageHeader,
  RelativeTime,
  SearchField,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ICON_STROKE,
} from "@optymyse/suite-ui";
import type { ContactSummary } from "../../shared/types/directory";
import { edits } from "../../shared/schemas/workspaces";
import { useContacts } from "../lib/directory";
import { arrange, columnSort, sortFrom, sortParam, type Column, type ListSort } from "../lib/listing";
import { workspaceSlug } from "../lib/hosts";
import { useWorkspace } from "../lib/workspaces";
import { fill, strings } from "../lib/strings";
import { ApiError } from "../lib/api";
import { DirectoryProblem, Page } from "../components/directory/Page";

const COLUMNS = ["name", "email", "phone", "company", "updated"] as const;
type ContactColumn = (typeof COLUMNS)[number];

const BY: Readonly<Record<ContactColumn, Column<ContactSummary>>> = {
  name: { kind: "name", value: (row) => row.display_name },
  email: { kind: "name", value: (row) => row.primary_email },
  phone: { kind: "name", value: (row) => row.primary_phone },
  company: { kind: "name", value: (row) => row.primary_company_name },
  updated: { kind: "time", value: (row) => row.updated_at },
};

type ContactsSearch = { q?: string; sort?: string };

/**
 * The contacts: the people at the organisation's customers, A to Z by name, searched and sorted as the customers are
 * (src/client/lib/listing.ts).
 */
function ContactsPage() {
  const { q = "", sort: sortText } = Route.useSearch();
  const navigate = Route.useNavigate();
  const view = useWorkspace(workspaceSlug());
  const contacts = useContacts();
  const sort = sortFrom(sortText, COLUMNS, "name");
  const mayEdit = view.data ? edits(view.data.role) : false;
  const search = (next: { q?: string; sort?: ListSort<ContactColumn> }) =>
    void navigate({
      search: (was) => {
        const nextQ = next.q ?? was.q ?? "";
        const nextSort = next.sort ? sortParam(next.sort, "name") : was.sort;
        return { ...(nextQ ? { q: nextQ } : {}), ...(nextSort ? { sort: nextSort } : {}) };
      },
      replace: true,
    });
  const all = contacts.data?.items ?? [];
  const rows = arrange(all, {
    q,
    sort,
    columns: BY,
    tiebreak: "name",
    searched: (row) => [
      row.display_name,
      row.given_name,
      row.family_name,
      row.preferred_name,
      row.primary_email,
      row.primary_phone,
      row.primary_company_name,
    ],
  });
  const head = (column: ContactColumn) => columnSort(sort, column, (next) => search({ sort: next }));

  return (
    <Page wide>
      <PageHeader
        title={strings.contacts.title}
        description={strings.contacts.description}
        actions={
          mayEdit ? (
            <Button asChild variant="primary">
              <RouterLink to="/admin/contacts/new">
                <Plus aria-hidden strokeWidth={ICON_STROKE} />
                {strings.contacts.add}
              </RouterLink>
            </Button>
          ) : undefined
        }
      />
      <div role="search" aria-label={strings.contacts.title} className="flex flex-wrap items-center gap-3">
        <SearchField value={q} onSearch={(next) => search({ q: next })} label={strings.contacts.search} delay={0} />
        {contacts.data && (
          <p className="text-secondary text-ink-muted" aria-live="polite">
            {q
              ? `${rows.length} of ${fill(strings.contacts.count, { count: all.length })}`
              : fill(strings.contacts.count, { count: all.length })}
          </p>
        )}
      </div>
      {contacts.data?.capped && (
        <Banner tone="warning">{fill(strings.directory.capped, { cap: contacts.data.cap })}</Banner>
      )}
      {contacts.isPending ? (
        <ListSkeleton rows={6} lines={1} label={strings.contacts.title} />
      ) : contacts.isError ? (
        <DirectoryProblem
          {...(contacts.error instanceof ApiError ? { message: contacts.error.message } : {})}
          onRetry={() => void contacts.refetch()}
        />
      ) : all.length === 0 ? (
        <EmptyState
          icon={ContactIcon}
          title={strings.contacts.empty}
          titleAs="h2"
          action={
            mayEdit ? (
              <Button asChild variant="primary">
                <RouterLink to="/admin/contacts/new">{strings.contacts.add}</RouterLink>
              </Button>
            ) : undefined
          }
        >
          {strings.contacts.emptyBody}
        </EmptyState>
      ) : (
        <Table>
          <TableCaption>{strings.contacts.title}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead sort={head("name")}>{strings.contacts.columns.name}</TableHead>
              <TableHead sort={head("email")}>{strings.contacts.columns.email}</TableHead>
              <TableHead sort={head("phone")}>{strings.contacts.columns.phone}</TableHead>
              <TableHead sort={head("company")}>{strings.contacts.columns.company}</TableHead>
              <TableHead sort={head("updated")}>{strings.contacts.columns.updated}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-ink-muted">
                  {fill(strings.contacts.noMatches, { q })}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <RouterLink
                      to="/admin/contacts/$contactId"
                      params={{ contactId: row.id }}
                      className="font-medium text-ink underline-offset-2 hover:underline"
                    >
                      {row.display_name}
                    </RouterLink>
                  </TableCell>
                  <TableCell className="text-ink-muted">{row.primary_email ?? ""}</TableCell>
                  <TableCell className="text-ink-muted">{row.primary_phone ?? ""}</TableCell>
                  <TableCell className="text-ink-muted">
                    {row.primary_company_id && row.primary_company_name ? (
                      <RouterLink
                        to="/admin/customers/$companyId"
                        params={{ companyId: row.primary_company_id }}
                        className="underline-offset-2 hover:underline"
                      >
                        {row.primary_company_name}
                      </RouterLink>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-ink-muted">
                    <RelativeTime at={row.updated_at} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </Page>
  );
}

export const Route = createFileRoute("/admin/contacts/")({
  validateSearch: (search: Record<string, unknown>): ContactsSearch => ({
    ...(typeof search.q === "string" && search.q !== "" ? { q: search.q } : {}),
    ...(typeof search.sort === "string" && search.sort !== "" ? { sort: search.sort } : {}),
  }),
  component: ContactsPage,
});
